import type { MapEventUpdateHandler } from '@yandex/ymaps3-types/imperative/YMapListener';
import React                     from 'react';

import './map.style.scss';

import { MapLayout, MapLoader, MapMarker } from './components';
import type { MapProps }                   from './map.props';
import type { IMapMarker }                 from './map.types';

const Map: React.FC<MapProps> = function(props) {
  const {
          markers,
          onMarkerClick,
          selected,
        } = props;

  const [active, setActive]     = React.useState(props.selected);
  const [center, setCenter]     = React.useState(props.center);
  const [location, setLocation] = React.useState<IMapMarker>();
  const [zoom, setZoom]         = React.useState(props.zoom);

  const activeMarker   = React.useMemo(() => {
    if (typeof active === 'undefined') {
      return;
    }

    return markers?.find((marker) => {
      return typeof active === 'number'
        ? marker.id === active
        : marker === active;
    });
  }, [active, markers]);
  const selectedMarker = React.useMemo(() => {
    if (typeof selected === 'undefined') {
      return;
    }

    return markers?.find((marker) => {
      return typeof selected === 'number'
        ? marker.id === selected
        : marker === selected;
    });
  }, [markers, selected]);

  const onUpdate = React.useCallback<MapEventUpdateHandler>((state) => {
    if (!state.mapInAction) {
      const [lng, lat] = state.location.center;

      setCenter([lng, lat]);
      setZoom(state.location.zoom);
    }
  }, []);

  React.useEffect(() => {
    if (window.location.protocol.startsWith('https')) {
      navigator.geolocation?.getCurrentPosition((position) => {
        if (position.coords) {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        }
      });
    }
  }, []);
  React.useEffect(() => {
    setCenter(props.center);
  }, [...props.center]);
  React.useEffect(() => {
    setActive(props.selected);
  }, [props.selected]);
  React.useEffect(() => {
    setZoom(props.zoom);
  }, [props.zoom]);

  return (
    <div
      className={'map'}
      onClick={() => {
        onMarkerClick?.();
      }}
    >
      <MapLoader>
        {(Yandex) => {
          return (
            <MapLayout
              Yandex={Yandex}
              center={activeMarker ? [activeMarker.lng, activeMarker.lat] : center}
              location={location}
              onUpdate={onUpdate}
              zoom={zoom}
            >
              {markers?.map((marker, key) => {
                const isActive = typeof selected === 'number'
                  ? selected === marker.id
                  : selected === marker;

                return (
                  <Yandex.YMapMarker
                    coordinates={Yandex.reactify.useDefault([marker.lng, marker.lat])}
                    key={marker.id ?? key}
                    onClick={(event) => {
                      event.stopPropagation();

                      onMarkerClick?.(marker);
                      setActive(marker);
                    }}
                  >
                    <MapMarker
                      active={isActive}
                      {...marker}
                    />
                  </Yandex.YMapMarker>
                );
              })}
              {typeof selectedMarker !== 'undefined' && (
                <Yandex.YMapMarker
                  coordinates={Yandex.reactify.useDefault([selectedMarker.lng, selectedMarker.lat])}
                  key={selectedMarker.id}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <MapMarker
                    active
                    selected
                    {...selectedMarker}
                  />
                </Yandex.YMapMarker>
              )}
            </MapLayout>
          );
        }}
      </MapLoader>
    </div>
  );
};

export default Map;